import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useWallet } from '../../contexts/WalletContext'
import { getAffiliateMe } from '../../services/affiliateService'
import { apiRequest } from '../../services/apiRequest'
import type { AffiliateMeData } from '../../services/affiliateService'

const AffiliateWithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const { formatCurrency } = useWallet()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [amount, setAmount] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const me = await getAffiliateMe()
        if (!cancelled) setAffiliateData(me)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const remaining = affiliateData?.remainingEarnings ?? 0
  const minWithdraw = 100

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseFloat(amount.replace(/,/g, ''))
    if (isNaN(num) || num < minWithdraw) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนไม่ถูกต้อง',
        text: `กรุณาระบุจำนวนขั้นต่ำ ${minWithdraw} บาท`
      })
      return
    }
    if (num > remaining) {
      Swal.fire({
        icon: 'warning',
        title: 'ยอดไม่เพียงพอ',
        text: `รายได้คงเหลือ ${formatCurrency(remaining)}`
      })
      return
    }
    setSubmitting(true)
    try {
      const res = await apiRequest<{ success: boolean; message?: string }>(
        '/api/v1/user-auth/affiliate/withdraw-request',
        { method: 'POST', body: { amount: num } }
      )
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'ส่งคำขอสำเร็จ',
          text: 'ระบบจะดำเนินการโอนเงินตามที่ระบุ'
        }).then(() => {
          setAmount('')
          navigate('/affiliate')
        })
      } else {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: (res as any).message || 'ไม่สามารถดำเนินการได้ในขณะนี้'
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: msg
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 col-lg-8 offset-lg-2 px-1">
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2">กำลังโหลดข้อมูล...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 col-lg-8 offset-lg-2 px-1">
              <div className="text-center py-3">
                <p className="c-red mb-2">{error}</p>
                <button type="button" className="check-button bgg-4" onClick={() => navigate('/affiliate')}>
                  กลับไปแนะนำเพื่อน
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 col-lg-8 offset-lg-2 px-1">
            <div className="text-center">
              <div className="bdr-5 bx-sd mb-2">
                <div className="w-100 p-2 bgc-4 wrap justify-content-between mb-2">
                  <a href="#" className="txt-link-d" onClick={(e) => { e.preventDefault(); navigate('/affiliate') }}>
                    <i className="fas fa-chevron-left me-1"></i>
                    กลับไปแนะนำเพื่อน
                  </a>
                </div>
              </div>
              <div className="row mx--1 mb-2">
                <div className="col-12 px-1">
                  <div className="bgg-1 bdr-5 bdc-1 p-2 text-center">
                    <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                      <small>รายได้คงเหลือ (บาท)</small>
                    </div>
                    <div className="px-1 t-regular c-white">
                      <h4>{formatCurrency(remaining)}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                <div className="fs-18 t-medium mb-2">
                  <i className="fas fa-dollar-sign me-2"></i>
                  ถอนคอมมิชชั่น
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="mb-2">
                    <label className="form-label text-start w-100">จำนวนเงิน (บาท)</label>
                    <input
                      type="text"
                      className="form-control bgc-7 bdr-5"
                      placeholder={`ขั้นต่ำ ${minWithdraw} บาท`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={remaining < minWithdraw}
                    />
                  </div>
                  <button
                    type="submit"
                    className="check-button bgg-4 c-white w-100"
                    disabled={submitting || remaining < minWithdraw}
                  >
                    {submitting ? 'กำลังส่งคำขอ...' : 'ส่งคำขอถอน'}
                  </button>
                </form>
                {remaining < minWithdraw && (
                  <p className="text-muted small mt-2">รายได้คงเหลือไม่ถึงขั้นต่ำในการถอน</p>
                )}
              </div>

              <button
                type="button"
                className="check-button bgg-1 bdc-1 c-white w-100"
                onClick={() => navigate('/affiliate')}
              >
                กลับไปแนะนำเพื่อน
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AffiliateWithdrawalPage
